// KV write-behind for the log ring (docs/10 §2, deliverable "persist the ring").
//
// The field incident's report began at app launch because the ring was memory
// only. Persisting it is the difference between "we have the four lines someone
// photographed" and "we have the session". A crash loop is exactly the case
// where the interesting lines are the ones a restart throws away.
//
// Writes are COALESCED rather than write-through. The ring is one KV value, so
// a write costs the whole ring; at one write per line a chatty second would
// spend more time in the Keychain-backed store than in the network stack it is
// trying to describe. The flush window is small (500 ms) and an `error` line
// flushes immediately, so the lines that matter to a crash are the lines least
// likely to be in flight when it happens.
//
// DURING A CALL the window widens to `RING_FLUSH_BUSY_MS`. On iOS `kv.put` is a
// synchronous JSC bridge call into the Keychain on the same thread as the call
// state machine, and a 64 KB write every 500 ms there spends the prime
// directive's budget (Connection > Latency > Quality) on diagnostics. Error
// lines still go through immediately, in a call or out of one: a crash is
// exactly when the window would otherwise be holding the interesting line.

import type { KvStore } from '../../storage/kv.js';
import { hydrateLogRing, logRing, setRingPersistence, type LogLine } from '../logRing.js';

export const DIAG_RING_KEY = 'merkury.diag.ring.v1';

/** Coalescing window. Short enough that a crash loses at most a blink. */
export const RING_FLUSH_DELAY_MS = 500;

/** Coalescing window while a call is up — an order of magnitude fewer
 *  whole-ring writes on the thread the call state machine runs on. */
export const RING_FLUSH_BUSY_MS = 5_000;

interface PersistedRing {
  v: 1;
  lines: LogLine[];
}

export interface RingStoreOptions {
  kv: KvStore;
  key?: string;
  flushDelayMs?: number;
  busyDelayMs?: number;
  /** True while a write must not compete with the call path (docs/10 §5 gates
   *  uploads on this same fact; the ring gates its WRITE RATE on it). */
  busy?: () => boolean;
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
}

/**
 * Owns the ring's storage slot. The CAPS live in `logRing.ts` (count and bytes,
 * oldest-first) and this class simply mirrors whatever survived them, so the
 * persisted copy can never be larger, or differently ordered, than the copy on
 * screen.
 */
export class RingStore {
  private readonly kv: KvStore;
  private readonly key: string;
  private readonly flushDelayMs: number;
  private readonly busyDelayMs: number;
  private readonly busy: () => boolean;
  private readonly setTimeoutFn: typeof setTimeout;
  private readonly clearTimeoutFn: typeof clearTimeout;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private writing: Promise<void> | null = null;
  private dirty = false;
  private stopped = false;

  constructor(options: RingStoreOptions) {
    this.kv = options.kv;
    this.key = options.key ?? DIAG_RING_KEY;
    this.flushDelayMs = options.flushDelayMs ?? RING_FLUSH_DELAY_MS;
    this.busyDelayMs = options.busyDelayMs ?? RING_FLUSH_BUSY_MS;
    this.busy = options.busy ?? ((): boolean => false);
    this.setTimeoutFn = options.setTimeoutFn ?? setTimeout;
    this.clearTimeoutFn = options.clearTimeoutFn ?? clearTimeout;
  }

  /**
   * Cold start: read the previous session's lines back into the ring, then take
   * over its persistence. Tolerant of every kind of garbage in the slot — a
   * diagnostics feature that can brick a boot is worse than no diagnostics.
   */
  async attach(): Promise<number> {
    const restored = await this.read();
    if (restored.length > 0) hydrateLogRing(restored);
    setRingPersistence(this);
    // The hydrate itself is worth persisting: the caps may have trimmed the
    // restored set, and the next cold start should inherit the trimmed one.
    this.note();
    return restored.length;
  }

  /** Stop mirroring. The slot keeps whatever was last written. */
  detach(): void {
    this.stopped = true;
    if (this.timer !== null) {
      this.clearTimeoutFn(this.timer);
      this.timer = null;
    }
    setRingPersistence(null);
  }

  /**
   * A line landed. Schedule a flush — except for an `error`, which is written
   * through: a crash loop is the case this whole module exists for, and the
   * line that explains it must not be the one still sitting in the window.
   */
  note(level?: string): void {
    if (this.stopped) return;
    this.dirty = true;
    if (level === 'error') {
      void this.flush();
      return;
    }
    if (this.timer !== null) return;
    const delay = this.busy() ? this.busyDelayMs : this.flushDelayMs;
    this.timer = this.setTimeoutFn(() => {
      this.timer = null;
      void this.flush();
    }, delay);
  }

  /** Write now. Safe to call at any time; serializes against itself. */
  async flush(): Promise<void> {
    if (this.timer !== null) {
      this.clearTimeoutFn(this.timer);
      this.timer = null;
    }
    if (this.writing) {
      await this.writing;
      if (!this.dirty) return;
    }
    if (!this.dirty) return;
    this.dirty = false;
    const payload: PersistedRing = { v: 1, lines: logRing() };
    this.writing = this.kv.put(this.key, payload).catch(() => {
      // A full or locked store must not take the brain down; the next line
      // re-marks the ring dirty and tries again.
      this.dirty = true;
    });
    const inFlight = this.writing;
    await inFlight;
    if (this.writing === inFlight) this.writing = null;
  }

  async clear(): Promise<void> {
    this.dirty = false;
    await this.kv.delete(this.key).catch(() => undefined);
  }

  private async read(): Promise<LogLine[]> {
    let raw: unknown;
    try {
      raw = await this.kv.get<unknown>(this.key);
    } catch {
      return [];
    }
    const lines = (raw as PersistedRing | undefined)?.lines;
    if (!Array.isArray(lines)) return [];
    const out: LogLine[] = [];
    for (const candidate of lines) {
      const line = candidate as Partial<LogLine> | null;
      if (
        line === null ||
        typeof line !== 'object' ||
        typeof line.ts !== 'number' ||
        typeof line.level !== 'string' ||
        typeof line.msg !== 'string'
      ) {
        continue;
      }
      out.push(
        line.event === undefined
          ? { ts: line.ts, level: line.level, msg: line.msg }
          : { ts: line.ts, level: line.level, msg: line.msg, event: line.event },
      );
    }
    return out;
  }
}
